import {
  CommutingTripService,
  GeocoderService,
  Geogroup,
  Place,
  TUserPlaceType,
  UserPlace,
  UserService,
  useCancellablePromise,
  userPlaceTypes,
} from '@geovelo-frontends/commons';
import { Box, CircularProgress, DialogProps, TextField, Typography } from '@mui/material';
import { Marker } from 'maplibre-gl';
import { useSnackbar } from 'notistack';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { AppContext } from '../../app/context';
import useTeams from '../../hooks/teams';
import usePartnerGeogroupQuery from '../../hooks/use-partner-geogroup-query';
import usePartnerSubgroupsQuery from '../../hooks/use-partner-subgroups-query';
import useUpdateGeogroupMutation from '../../hooks/use-update-geogroup-mutation';
import { Button } from '../button';
import { Dialog } from '../dialog';

import PlaceForm from './form';
import WorkForm, { TSelectedSite, noCorrespondingSiteItem } from './work-form';

const defaultMapId = 'place-map';

function PlaceDialog({
  mapId: _mapId,
  disableDescription,
  placeType,
  currentPlace,
  ...props
}: Omit<DialogProps, 'onClose'> & {
  currentPlace: UserPlace | Place | null;
  disableDescription?: boolean;
  mapId?: string;
  onClose: (place?: UserPlace | Place) => void;
  placeType: Extract<TUserPlaceType, 'home' | 'work'> | 'company';
}): React.JSX.Element {
  const [title, setTitle] = useState('');
  const [selectedSite, selectSite] = useState<TSelectedSite>(null);
  const [updatedPlace, updatePlace] = useState<UserPlace | Place | null>(null);
  const [updated, setUpdated] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const markerRef = useRef<Marker | null>(null);
  const {
    partner: { current: currentPartner },
    user: { current: currentUser, employee, home, works, site: userSite },
    actions: { setUserHome, setUserWorks, setUserCommutingTrips },
  } = useContext(AppContext);
  const { data: geogroup } = usePartnerGeogroupQuery(currentPartner);
  const { mutateAsync: updateGeogroup } = useUpdateGeogroupMutation();
  const { data: partnerSubgroups } = usePartnerSubgroupsQuery(currentPartner);
  const sites = partnerSubgroups?.sites;
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { cancellablePromise, cancelPromises } = useCancellablePromise();
  const { updateSite } = useTeams();

  useEffect(() => {
    if (props.open) {
      setUpdated(false);
      setSubmitting(false);

      if (placeType === 'work') {
        selectSite(
          currentPlace
            ? (currentPlace instanceof UserPlace &&
                sites?.find(
                  ({ place }) => place && place.strPoint.localeCompare(currentPlace.strPoint) === 0,
                )) ||
                noCorrespondingSiteItem
            : null,
        );
      } else {
        selectSite(null);
      }

      setTitle((currentPlace && currentPlace instanceof UserPlace && currentPlace.title) || '');
      updatePlace(currentPlace?.clone() || null);
    }
  }, [props.open]);

  useEffect(() => {
    async function reverseGeocode() {
      if (updatedPlace && !updatedPlace.addressDetail) {
        try {
          const newPlace = await cancellablePromise(
            GeocoderService.reverseGeocode(undefined, updatedPlace.point),
          );
          updatePlace(newPlace);
        } catch (err) {
          console.error(err);
        }
      }
    }

    reverseGeocode();

    return () => cancelPromises();
  }, [updatedPlace]);

  async function handleSubmit() {
    if (!currentPartner || !currentUser || !updatedPlace) return;

    if (placeType === 'company') {
      if (!geogroup) return;

      setSubmitting(true);
      markerRef.current?.setDraggable(false);

      try {
        await updateGeogroup({ id: geogroup.id, partner: currentPartner, address: updatedPlace });

        enqueueSnackbar(t('companies.pages.admin.company.data.general_data_form.success'));
        props.onClose();
      } catch (err) {
        console.error(err);
        setSubmitting(false);
        markerRef.current?.setDraggable(true);
        enqueueSnackbar(t('companies.pages.admin.company.data.general_data_form.server_error'), {
          variant: 'error',
        });
      }
    } else {
      const currentUserPlace =
        placeType === 'home'
          ? home
          : works
            ? works.find(({ id }) => id !== -1 && currentPlace?.id === id) || null
            : undefined;

      if (currentUserPlace === undefined) return;

      const { point, address } = updatedPlace;

      setSubmitting(true);
      markerRef.current?.setDraggable(false);

      try {
        let newPlace: UserPlace | undefined;
        if (currentUserPlace) {
          if (updated) {
            const { id } = currentUserPlace;
            if (typeof id === 'number') {
              newPlace = await UserService.updatePlace(id, {
                point,
                address,
                title: title || t(userPlaceTypes[placeType].titleKey || ''),
                partnerId: placeType === 'work' ? currentPartner.id : undefined,
              });

              if (placeType === 'home') setUserHome(newPlace);
              else if (works) {
                const newWorks = [...works];
                const index = newWorks.findIndex((work) => id === work.id);
                if (index > -1) {
                  newWorks.splice(index, 1, newPlace);
                  setUserWorks(newWorks);
                }
              }
            }
            enqueueSnackbar(t('companies.pages.admin.user.address_form.success_update'));
          } else {
            newPlace = currentUserPlace;
          }
        } else {
          newPlace = await UserService.addPlace({
            type: placeType,
            point,
            address,
            title: title || t(userPlaceTypes[placeType].titleKey || ''),
            partnerId: placeType === 'work' ? currentPartner.id : undefined,
          });

          if (placeType === 'home') {
            if (employee) {
              try {
                setUserCommutingTrips(
                  await CommutingTripService.getCommutingTrips({ employeeId: employee.id }),
                );
              } catch (err) {
                console.error(err);
              }
            }
            setUserHome(newPlace);
          } else if (works) {
            setUserWorks([...works, newPlace]);
          }

          enqueueSnackbar(t('companies.pages.admin.user.address_form.success_creation'));
        }

        if (placeType === 'work' && userSite === null && selectedSite instanceof Geogroup) {
          await updateSite({
            partnerCode: currentPartner.code,
            userId: currentUser.id,
            prevSite: null,
            newSiteId: selectedSite.id,
          });
        }

        props.onClose(newPlace);
      } catch (err) {
        console.error(err);
        setSubmitting(false);
        markerRef.current?.setDraggable(true);
        enqueueSnackbar(
          t(
            currentUserPlace
              ? 'companies.pages.admin.user.address_form.server_error_update'
              : 'companies.pages.admin.user.address_form.server_error_creation',
          ),
          {
            variant: 'error',
          },
        );
      }
    }
  }

  const mapId = _mapId || defaultMapId;

  return (
    <Dialog disablePadding fullWidth loading={isSubmitting} maxWidth="sm" {...props}>
      <Box display="flex" flexDirection="column" gap={3} padding={3}>
        <Typography fontSize="1.25rem" fontWeight={600}>
          {placeType === 'company'
            ? t(
                currentPlace
                  ? 'companies.pages.admin.company.data.address_form.title_update'
                  : 'companies.pages.admin.company.data.address_form.title_creation',
              )
            : t(
                placeType === 'home'
                  ? currentPlace
                    ? 'companies.pages.admin.user.address_form.title_home_update'
                    : 'companies.pages.admin.user.address_form.title_home_creation'
                  : currentPlace
                    ? 'companies.pages.admin.user.address_form.title_work_update'
                    : 'companies.pages.admin.user.address_form.title_work_creation',
              )}
        </Typography>
        {!disableDescription && placeType !== 'company' && (
          <Typography sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }} variant="body2">
            {t(
              placeType === 'work'
                ? sites && sites.length === 0
                  ? 'companies.pages.admin.user.address_form.description_work'
                  : 'companies.pages.admin.user.address_form.description_site'
                : 'companies.pages.admin.user.address_form.description',
            )
              .split('\n')
              .map((line, index) => (
                <span key={index}>{line}</span>
              ))}
          </Typography>
        )}
        {placeType !== 'company' && (
          <Box display="flex" flexDirection="column" gap={0.5}>
            <Typography fontWeight={700} variant="body2">
              {t('commons.name')}
            </Typography>
            <TextField
              disabled={isSubmitting}
              onChange={({ currentTarget }) => {
                setTitle(currentTarget.value);
                setUpdated(true);
              }}
              placeholder={t(userPlaceTypes[placeType].titleKey || '')}
              size="small"
              slotProps={{ inputLabel: { shrink: true } }}
              value={title}
              variant="outlined"
            />
          </Box>
        )}
        {placeType === 'work' ? (
          <WorkForm
            initialized={props.open}
            initialPlace={currentPlace}
            isSubmitting={isSubmitting}
            mapId={mapId}
            markerRef={markerRef}
            onPlaceUpdated={updatePlace}
            selectedSite={selectedSite}
            selectSite={selectSite}
            setUpdated={setUpdated}
            updatedPlace={updatedPlace}
          />
        ) : (
          <PlaceForm
            initialized={props.open}
            initialPlace={currentPlace}
            isSubmitting={isSubmitting}
            mapId={mapId}
            markerRef={markerRef}
            onPlaceUpdated={updatePlace}
            placeType={placeType}
            setUpdated={setUpdated}
            updatedPlace={updatedPlace}
          />
        )}
        <Box alignItems="center" display="flex" gap={2} justifyContent="flex-end">
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={() => props.onClose()}
            variant="outlined"
          >
            {t('commons.actions.cancel')}
          </Button>
          <Button
            color="primary"
            disabled={!updatedPlace || isSubmitting}
            onClick={handleSubmit}
            startIcon={isSubmitting && <CircularProgress color="inherit" size={16} thickness={4} />}
            variant="contained"
          >
            {t('commons.actions.validate')}
          </Button>
        </Box>
      </Box>
    </Dialog>
  );
}

export default PlaceDialog;
